import { useEffect, useRef, useState } from "react";
import { endpoints, formatApiError } from "../lib/api";
import { Microphone, Stop, CircleNotch, WarningCircle } from "@phosphor-icons/react";

// Domain vocabulary so Whisper spells restoration terms correctly.
export const RESTORATION_VOCAB =
    "SERVPRO, MSA, master service agreement, supplier list, adjuster, TPA, Xactimate, ROM, mitigation, " +
    "remediation, mould, water damage, fire damage, property manager, facilities manager, work authorization";

/**
 * Big record button + timer. Records in the browser, sends to the existing
 * /voice/transcribe (Whisper) endpoint, hands back text.
 */
export default function VoiceCapture({ onTranscribed, maxSeconds = 300, label = "Record", size = "lg", disabled }) {
    const [recording, setRecording] = useState(false);
    const [busy, setBusy] = useState(false);
    const [seconds, setSeconds] = useState(0);
    const [busySeconds, setBusySeconds] = useState(0);
    const [error, setError] = useState("");
    const recRef = useRef(null);
    const chunksRef = useRef([]);
    const streamRef = useRef(null);
    const timerRef = useRef(null);
    const busyRef = useRef(null);

    useEffect(() => () => {
        clearInterval(timerRef.current);
        clearInterval(busyRef.current);
        streamRef.current?.getTracks().forEach((t) => t.stop());
    }, []);

    async function send(blob, retry) {
        const fd = new FormData();
        fd.append("file", blob, "visit-note.webm");
        fd.append("prompt", RESTORATION_VOCAB);
        try { return await endpoints.transcribeVoice(fd); }
        catch (e) {
            if (!retry && (e.code === "ECONNABORTED" || !e.response)) return send(blob, true);
            throw e;
        }
    }

    async function onStop() {
        setBusy(true);
        setBusySeconds(0);
        let n = 0;
        busyRef.current = setInterval(() => setBusySeconds(++n), 1000);
        try {
            const blob = new Blob(chunksRef.current, { type: recRef.current?.mimeType || "audio/webm" });
            const { data } = await send(blob, false);
            if (data?.text) onTranscribed(data.text);
            else setError("Didn't catch any speech — try again.");
        } catch (e) { setError(formatApiError(e) || "Couldn't transcribe that recording."); }
        finally { clearInterval(busyRef.current); setBusy(false); }
    }

    function stop() {
        recRef.current?.stop();
        streamRef.current?.getTracks().forEach((t) => t.stop());
        clearInterval(timerRef.current);
        setRecording(false);
    }

    async function start() {
        setError("");
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            const mimeType = window.MediaRecorder?.isTypeSupported?.("audio/webm") ? "audio/webm" : "";
            const rec = new MediaRecorder(stream, mimeType ? { mimeType, audioBitsPerSecond: 32000 } : { audioBitsPerSecond: 32000 });
            chunksRef.current = [];
            rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
            rec.onstop = onStop;
            rec.start();
            recRef.current = rec;
            setSeconds(0);
            let n = 0;
            timerRef.current = setInterval(() => { n += 1; setSeconds(n); if (n >= maxSeconds) stop(); }, 1000);
            setRecording(true);
        } catch { setError("Microphone access denied — check your browser permissions."); }
    }

    const left = Math.max(0, maxSeconds - seconds);
    const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
    const big = size === "lg";

    return (
        <div className={`flex items-center gap-3 flex-wrap ${big ? "" : "text-xs"}`} data-testid="voice-capture">
            <button type="button" onClick={recording ? stop : start} disabled={disabled || busy} data-testid="voice-capture-btn"
                className={`rounded-full flex items-center justify-center border-2 transition-colors shrink-0 ${big ? "w-14 h-14" : "w-8 h-8"} ${
                    recording ? "bg-red-600 border-red-600 text-white animate-pulse" : "bg-white border-[var(--rom-navy)] text-[var(--rom-navy)] hover:bg-slate-50"}`}
                title={recording ? "Stop" : label}>
                {busy ? <CircleNotch size={big ? 22 : 14} className="animate-spin" /> : recording ? <Stop size={big ? 20 : 12} weight="fill" /> : <Microphone size={big ? 24 : 14} weight="bold" />}
            </button>
            <div className="min-w-0">
                {recording ? (
                    <div className="text-sm font-semibold text-red-600 flex items-center gap-1.5" data-testid="voice-capture-recording">
                        <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse" /> Recording {mmss(seconds)} · {mmss(left)} left — tap to stop
                    </div>
                ) : busy ? (
                    <div className="text-sm text-[var(--rom-text-2)]" data-testid="voice-capture-busy">Transcribing… {busySeconds}s</div>
                ) : big ? (
                    <div className="text-sm text-[var(--rom-text-2)]"><b className="text-[var(--rom-text)]">{label}</b> — up to {Math.round(maxSeconds / 60)} minute{Math.round(maxSeconds / 60) === 1 ? "" : "s"}</div>
                ) : null}
                {error && <div className="text-xs text-red-600 flex items-center gap-1 mt-0.5"><WarningCircle size={12} weight="bold" /> {error}</div>}
            </div>
        </div>
    );
}
